<!--
  ViewInternalDashboard.vue — REPLACES the current scaffold.
  Drop into web/src/views/ViewInternalDashboard.vue
  NOTE: not compile-verified (no Vue build) — Manish to test locally.
  Lists the internal dashboards the logged-in user may see (server decides), then
  renders the chosen one in a sandboxed iframe, injecting its data as
  window.__DASHBOARD_DATA__.payload (same convention as the client dashboards).
  Grid: a vs-table for reliability — swap to the project's AG grid if preferred.
-->
<template>
  <div class="vx-row">
    <div class="vx-col w-full mb-base">
     

      <!-- RENDER -->
      <vx-card>
        <vs-row vs-w="12" class="mb-5">
          <vs-col vs-lg="2"><vs-button color="danger" type="border" icon="keyboard_backspace" @click="back">Back</vs-button></vs-col>
          <vs-col vs-type="flex" vs-justify="center" vs-align="center" vs-lg="8"><h3>{{ selected.name }}</h3></vs-col>
          <vs-col vs-type="flex" vs-justify="flex-end" vs-align="center" vs-lg="2">
            <vs-button
              v-if="refreshable && !unavailable"
              size="small"
              type="border"
              icon="refresh"
              :disabled="refreshing"
              @click="refresh"
            >{{ refreshing ? "Refreshing…" : "Refresh" }}</vs-button>
          </vs-col>
        </vs-row>
        <p v-if="updatedAt && !unavailable && !loadingFrame" class="text-sm mb-2">
          Last updated {{ formatUtc(updatedAt) }} · all times UTC
        </p>
        <p v-if="refreshNote" class="text-sm mb-2">{{ refreshNote }}</p>
        <div v-if="loadingFrame" class="text-center p-10">Loading…</div>
        <div v-if="unavailable && !loadingFrame" class="text-center p-10">
          This dashboard is temporarily unavailable. Please try again shortly.
        </div>
        <iframe
          v-if="!loadingFrame && !unavailable"
          :srcdoc="srcdoc"
          class="internal_frame"
          sandbox="allow-scripts allow-popups"
          frameborder="0"
        ></iframe>
      </vx-card>
    </div>
  </div>
</template>

<script>
import axios from "@/axios.js";
import ClientDataGrid from "@/components/aggrid/AggridClient.vue";
import defineAbilitiesFor from "@/config/abilities";

// Refresh (emp-dash only): emp-dash picks up a request within 30 s and computes in
// 1-5 s, so a refresh normally lands in under 35 s. It can also legitimately never
// land (a failed, held or dependency-blocked run keeps the previous figures), so the
// poll gives up after POLL_GIVE_UP_MS and says so.
const POLL_INTERVAL_MS = 5000;
const POLL_GIVE_UP_MS = 60000;

export default {
   created() {
    var self = this;
    defineAbilitiesFor().then(function (data) {
      self.abilities = data;
    });
    let userInfo = JSON.parse(localStorage.getItem("userInfo"));
    self.user_id = userInfo["uid"];
  },
  components: {
    ClientDataGrid
  },
  data() {
    return {
      dashboards: [],
      selected: {},
      srcdoc: "",
      loadingFrame: false,
      html: "",
      unavailable: false,   // any failure: one calm message, never the error code
      updatedAt: null,
      refreshable: false,   // only when the page came from emp-dash and is not final
      refreshing: false,    // the button is disabled while polling - no second run
      refreshNote: "",
      destroyed: false
    };
  },
  methods: {
    api(path) { return this.$base_url + "/api/internal_dashboard/" + path; },
    fetchDashboard(d) {
      axios.get(this.api("edit_internal_dashboard_data/" + d)).then(r => {
        this.selected = r.data.dashboard;

      }).catch(() => { });
    },
    open(d) {
      let self = this;
      self.selected = d;
      self.loadingFrame = true;
      self.unavailable = false;
      self.srcdoc = "";
      const base = this.$base_url + "/api/internal_dashboard/";
      Promise.all([
        axios.get(base + "serve_internal_dashboard/" + d),
        axios.get(base + "internal_dashboard_data/" + d)
      ])
        .then(([htmlRes, dataRes]) => {
          self.html = htmlRes.data;
          self.applyData(dataRes.data);
          self.loadingFrame = false;
        })
        .catch(() => {
          // Same message whatever went wrong; the server log records which failure it was.
          self.unavailable = true;
          self.loadingFrame = false;
        });
    },
    applyData(data) {
      const d = data || {};
      this.updatedAt = d.updated_at || null;
      this.refreshable = d.source === "emp-dash" && d.refreshable === true;
      // The dashboard HTML gets exactly what it got before: { payload, updated_at }.
      this.srcdoc = this.buildSrcdoc(this.html, { payload: d.payload, updated_at: d.updated_at });
    },
    refresh() {
      if (this.refreshing) return;
      const self = this;
      const d = this.$route.params.internalDashboardId;
      const before = this.updatedAt;
      self.refreshing = true;
      self.refreshNote = "Refreshing… this usually takes under a minute.";
      axios.post(this.$base_url + "/api/internal_dashboard/internal_dashboard_refresh/" + d)
        .then(() => self.poll(d, before, Date.now()))
        .catch(err => {
          const status = err && err.response && err.response.status;
          if (status === 409) {
            self.refreshable = false;
            self.refreshNote = "This dashboard is final and is not refreshed.";
          } else {
            self.refreshNote = "Refresh is temporarily unavailable. The figures shown are unchanged.";
          }
          self.refreshing = false;
        });
    },
    poll(d, before, started) {
      const self = this;
      if (self.destroyed) return;
      if (Date.now() - started > POLL_GIVE_UP_MS) {
        self.refreshing = false;
        self.refreshNote = "No newer figures yet. Still showing the figures from " + self.formatUtc(before) + ".";
        return;
      }
      setTimeout(() => {
        if (self.destroyed) return;
        axios.get(self.$base_url + "/api/internal_dashboard/internal_dashboard_data/" + d)
          .then(r => {
            if (r.data && r.data.updated_at && r.data.updated_at !== before) {
              self.applyData(r.data);
              self.refreshing = false;
              self.refreshNote = "Updated.";
            } else {
              self.poll(d, before, started);
            }
          })
          // a failed poll leaves the current figures on screen and keeps waiting
          .catch(() => self.poll(d, before, started));
      }, POLL_INTERVAL_MS);
    },
    formatUtc(iso) {
      if (!iso) return "";
      const t = new Date(iso);
      if (isNaN(t.getTime())) return iso;
      return t.toISOString().slice(0, 16).replace("T", " ") + " UTC";
    },
    buildSrcdoc(html, data) {
      var open = "<" + "script>";
      var close = "<" + "/script>";
      var inject = open + "window.__DASHBOARD_DATA__ = " + JSON.stringify(data || {}) + ";" + close;
      return inject + (html || "");
    },
    back() {
      this.$router.push("/list_view_internal_dashboard")
    }
  },
  mounted() {
    this.fetchDashboard(this.$route.params.internalDashboardId)
    this.open(this.$route.params.internalDashboardId);
  },
  beforeDestroy() {
    this.destroyed = true;
  }
};
</script>

<style>
.internal_frame {
  width: 100%;
  height: 80vh;
  border: none;
}
</style>
